import React from "react";
import { Box } from "@mui/joy";
import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import CommonModal from "../../../common/CommonModal";
import DarkInput from "../../../common/DarkInput";
import DarkTypography from "../../../common/DarkTypography";
import { mailPlaceholderValueTypesForLinkHrefField } from "../mailPlaceholderContext";
import { textareaOrInputPlaceholderDropProps } from "../mailEditorDnD";
import { DEFAULT_MAIL_EDITOR_LINK_HREF, mailPhToken } from "../mailLineParts";
import MailEditorPlaceholderChips from "./MailEditorPlaceholderChips";

export type MailEditorLinkUrlModalProps = {
  open: boolean;
  onClose: () => void;
  initialHref?: string;
  onConfirm: (href: string) => void;
  title?: string;
  urlPlaceholderChips?: MailPlaceholderDefinition[];
  labelLookupCatalog?: MailPlaceholderDefinition[];
  canEdit?: boolean;
};

export default function MailEditorLinkUrlModal(props: MailEditorLinkUrlModalProps) {
  const {
    open,
    onClose,
    initialHref = DEFAULT_MAIL_EDITOR_LINK_HREF,
    onConfirm,
    title = "Adresse du lien",
    urlPlaceholderChips = [],
    labelLookupCatalog,
    canEdit = true,
  } = props;
  const [draft, setDraft] = React.useState(initialHref);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const hrefFieldDropProps =
    labelLookupCatalog && labelLookupCatalog.length > 0
      ? textareaOrInputPlaceholderDropProps(draft, setDraft, {
          catalog: labelLookupCatalog,
          allowedValueTypes: mailPlaceholderValueTypesForLinkHrefField(),
          allowUnknownPlaceholderKey: true,
        })
      : textareaOrInputPlaceholderDropProps(draft, setDraft);

  React.useEffect(() => {
    if (open) {
      setDraft(initialHref);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [open, initialHref]);

  const handleConfirm = () => {
    const href = draft.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;

    onConfirm(href);
    onClose();
  };

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      onConfirm={handleConfirm}
      confirmLabel="Appliquer le lien"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {urlPlaceholderChips.length > 0 ? (
          <Box sx={{ minWidth: 0 }}>
            <DarkTypography level="body-xs" colorVariant="muted" sx={{ mb: 0.35 }}>
              Variables
            </DarkTypography>
            <MailEditorPlaceholderChips
              placeholdersForInsert={urlPlaceholderChips}
              labelLookupCatalog={labelLookupCatalog}
              canEdit={canEdit}
              onInsertPlaceholder={(key) => setDraft(mailPhToken(key))}
            />
          </Box>
        ) : null}
        <DarkInput
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          fullWidth
          placeholder={DEFAULT_MAIL_EDITOR_LINK_HREF}
          slotProps={{
            input: {
              ...hrefFieldDropProps,
              ref: inputRef,
              "aria-label": "URL ou jeton du lien",
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleConfirm();
                }
              },
            },
          }}
        />
      </Box>
    </CommonModal>
  );
}
